/**
 * ---------------------------------------------------------------------------------------------------------------------
 * FRG STICKY BUY BUTTONS
 * ---------------------------------------------------------------------------------------------------------------------
 *
 * <frg-sticky-buy-buttons form="product-form-ID" data-trigger="out_of_view|scrolled_past">
 * Markup: snippets/frg-sticky-buy-buttons.liquid — Styles: assets/frg-sticky-buy-buttons.css
 *
 * Visibility
 *   One IntersectionObserver watches the ORIGINAL purchase area (.product-info__buy-buttons, the wrapper of the main
 *   form) and the footer. No scroll listeners.
 *   - "out_of_view"   : the bar is visible whenever the purchase area is outside the viewport (above OR below it).
 *   - "scrolled_past" : the bar is visible only once the purchase area has been scrolled past (it is above the viewport).
 *   In both modes the bar is hidden while the footer is in view.
 *
 * Cart logic: none of its own
 *   - "Add to cart" is a submit button bound to the main form via the `form` attribute → the theme's ProductForm
 *     (<form is="product-form">) runs checkValidity/reportValidity, the aria-busy loader, /cart/add.js, the
 *     variant:add / cart:change events (cart drawer) and cart:error (banners rendered by <buy-buttons>).
 *   - "Buy it now" clicks the main form's Shopify dynamic checkout "Buy it now" button. When that button cannot be
 *     triggered (dynamic checkout disabled in the Buy buttons block, or only a branded wallet is rendered, which lives
 *     in a cross-origin iframe), the main form is submitted natively with return_to=/checkout.
 *
 * Variant / size validation
 *   Before anything reaches the cart, the selection is checked. If an option has no selected value (e.g. the custom
 *   Skechers picker, which can leave a size empty and blanks the variant ID), the action is cancelled, the theme's own
 *   cart:error banner is shown with "Please select a size", and the option is scrolled into view and focused.
 *   The same guard is applied to the ORIGINAL form's submit, so both entry points behave identically.
 *
 * Sync
 *   - Native <variant-picker>: the bar sits in a <product-rerender>, so the theme re-renders it on each change.
 *   - Any picker that emits `variant:change` on the form (incl. the Skechers picker): state is updated from
 *     event.detail.variant.
 *
 * Exposes --frg-sticky-buy-buttons-offset on <html> (distance from the viewport bottom to the top of the bar) while the
 * bar is visible, so that other floating UI (back to top button) can sit above it.
 */

const ERROR_THROTTLE_MS = 1200;
const OFFSET_VARIABLE = '--frg-sticky-buy-buttons-offset';

class FrgStickyBuyButtons extends HTMLElement {
  #abortController = null;
  #formAbortController = null;
  #intersectionObserver = null;
  #resizeObserver = null;
  #observedTarget = null;
  #footer = null;
  #targetVisible = true;
  #targetAbove = false;
  #footerInView = false;
  #lastErrorAt = 0;

  connectedCallback() {
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.formId = this.getAttribute('form');
    this.addButton = this.querySelector('button[type="submit"]');
    this.buyNowButton = this.querySelector('button[type="button"]');

    this.#intersectionObserver = new IntersectionObserver(this.#onIntersection.bind(this));
    this.#resizeObserver = new ResizeObserver(() => this.#updateOffsetVariable());
    this.#resizeObserver.observe(this);

    this.#footer = document.querySelector('.shopify-section-group-footer-group');

    if (this.#footer) {
      this.#intersectionObserver.observe(this.#footer);
    }

    this.#bindToForm();

    // Set the initial state synchronously. This matters when <product-rerender> replaces the bar while it is visible:
    // the new element is painted directly in its final state instead of fading out and back in.
    if (this.#observedTarget) {
      const rect = this.#observedTarget.getBoundingClientRect();
      this.#targetVisible = rect.bottom > 0 && rect.top < window.innerHeight;
      this.#targetAbove = rect.bottom <= 0;
    }

    if (this.#footer) {
      const footerRect = this.#footer.getBoundingClientRect();
      this.#footerInView = footerRect.top < window.innerHeight && footerRect.bottom > 0;
    }

    this.#updateVisibility();

    this.buyNowButton?.addEventListener('click', this.#onBuyNowClick.bind(this), { signal });

    // Variant changes emitted by any picker bound to this form. variant:change bubbles from the form.
    document.addEventListener('variant:change', this.#onVariantChange.bind(this), { signal });

    // Guard every submission of the main form (original button AND sticky button). A capture listener on the document
    // runs before the theme's ProductForm listener on the form itself, so an incomplete selection never reaches
    // /cart/add.js.
    document.addEventListener('submit', this.#onFormSubmit.bind(this), { signal, capture: true });
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#formAbortController?.abort();
    this.#intersectionObserver?.disconnect();
    this.#resizeObserver?.disconnect();
    this.#observedTarget = null;
    document.documentElement.style.removeProperty(OFFSET_VARIABLE);
  }

  get form() {
    return document.forms[this.formId] || null;
  }

  get options() {
    try {
      return JSON.parse(this.dataset.options || '[]');
    } catch (error) {
      return [];
    }
  }

  /* -------------------------------------------------------------------------------------------------------------------
   * Visibility
   * ----------------------------------------------------------------------------------------------------------------- */

  /**
   * (Re)attach to the main product form and observe its purchase area. Called on connect and after every
   * product:rerender, because a product change (combined listings) replaces the whole product info, form included.
   */
  #bindToForm() {
    const form = this.form;

    if (!form) {
      return;
    }

    const target = form.closest('.product-info__buy-buttons') || form;

    if (target !== this.#observedTarget) {
      if (this.#observedTarget) {
        this.#intersectionObserver.unobserve(this.#observedTarget);
      }

      this.#observedTarget = target;
      this.#intersectionObserver.observe(target);
    }

    // product:rerender is dispatched (without bubbling) on the form itself by the theme's VariantPicker.
    this.#formAbortController?.abort();
    this.#formAbortController = new AbortController();
    form.addEventListener('product:rerender', () => requestAnimationFrame(() => this.isConnected && this.#bindToForm()), {
      signal: this.#formAbortController.signal
    });
  }

  #onIntersection(entries) {
    entries.forEach((entry) => {
      if (entry.target === this.#observedTarget) {
        this.#targetVisible = entry.isIntersecting;
        this.#targetAbove = !entry.isIntersecting && entry.boundingClientRect.bottom <= 0;
      } else if (entry.target === this.#footer) {
        this.#footerInView = entry.isIntersecting;
      }
    });

    // The observed target may have been detached by a re-render. Re-bind lazily.
    if (this.#observedTarget && !this.#observedTarget.isConnected) {
      this.#bindToForm();
    }

    this.#updateVisibility();
  }

  #updateVisibility() {
    const targetHidden = this.dataset.trigger === 'scrolled_past' ? this.#targetAbove : !this.#targetVisible;
    const visible = Boolean(this.#observedTarget) && targetHidden && !this.#footerInView;

    this.classList.toggle('is-visible', visible);
    this.#updateOffsetVariable();
  }

  /** Expose the space taken by the bar at the bottom of the viewport, so floating UI can sit above it. */
  #updateOffsetVariable() {
    if (this.classList.contains('is-visible')) {
      const bottom = parseFloat(window.getComputedStyle(this).bottom) || 0;
      document.documentElement.style.setProperty(OFFSET_VARIABLE, `${Math.ceil(this.offsetHeight + bottom)}px`);
    } else {
      document.documentElement.style.removeProperty(OFFSET_VARIABLE);
    }
  }

  /* -------------------------------------------------------------------------------------------------------------------
   * Variant sync
   * ----------------------------------------------------------------------------------------------------------------- */

  #onVariantChange(event) {
    // Note: form.id would return the <input name="id">, so the attribute is read explicitly.
    if (!(event.target instanceof HTMLFormElement) || event.target.getAttribute('id') !== this.formId) {
      return;
    }

    const variant = event.detail?.variant;

    if (!variant || !variant.id) {
      // No variant: either an option is not selected yet (buttons stay enabled and ask for the selection on click), or
      // the combination does not exist.
      this.#setState(this.#getMissingOption() ? 'needs_selection' : 'unavailable');
    } else {
      this.#setState(variant.available ? 'available' : 'sold_out');
    }
  }

  #setState(state) {
    if (!this.addButton) {
      return;
    }

    const disabled = state === 'sold_out' || state === 'unavailable';
    const labels = {
      available: this.dataset.labelAdd,
      needs_selection: this.dataset.labelAdd,
      sold_out: this.dataset.labelSoldOut,
      unavailable: this.dataset.labelUnavailable
    };

    const label = this.addButton.querySelector('[data-sticky-label]');

    if (label) {
      label.textContent = labels[state];
    }

    this.addButton.disabled = disabled;

    if (this.buyNowButton) {
      this.buyNowButton.disabled = disabled;
    }

    // Same visual rules as snippets/button.liquid: a disabled button drops the secondary style and becomes subdued
    // (unless the merchant set custom colors).
    this.addButton.classList.toggle('button--secondary', this.hasAttribute('data-secondary-add') && !disabled);

    if (!this.hasAttribute('data-custom-colors')) {
      this.addButton.classList.toggle('button--subdued', disabled);
    }

    this.dataset.state = state;
  }

  /* -------------------------------------------------------------------------------------------------------------------
   * Validation
   * ----------------------------------------------------------------------------------------------------------------- */

  /**
   * Returns { name, element, focusable } for the first option that has no selected value, or null when the selection
   * is complete. Supports the theme's native <variant-picker> (radio inputs bound to the form) and
   * <skechers-variant-picker>.
   */
  #getMissingOption() {
    const form = this.form;

    if (!form) {
      return null;
    }

    const options = this.options;

    // Native picker: radio groups named "{form-id}-option{position}", associated to the form via the form attribute.
    const groups = new Map();

    Array.from(form.elements).forEach((element) => {
      if (element.matches('input[type="radio"][data-option-position]')) {
        const group = groups.get(element.name) || { position: Number(element.dataset.optionPosition), inputs: [] };
        group.inputs.push(element);
        groups.set(element.name, group);
      }
    });

    for (const group of groups.values()) {
      if (!group.inputs.some((input) => input.checked)) {
        return {
          name: options[group.position - 1] || '',
          element: group.inputs[0].closest('fieldset') || group.inputs[0].parentElement,
          focusable: group.inputs.find((input) => !input.disabled)
        };
      }
    }

    // Skechers picker: one .vp-option-group per option, the selected value carries .is-selected.
    const skechersPicker = document.querySelector(`skechers-variant-picker[data-product-form-id="${CSS.escape(this.formId)}"]`);

    if (skechersPicker) {
      for (const group of skechersPicker.querySelectorAll('.vp-option-group')) {
        if (!group.querySelector('.is-selected')) {
          return {
            name: group.getAttribute('data-option-name') || options[Number(group.dataset.optionIndex)] || '',
            element: group,
            focusable: group.querySelector('[data-option-value]:not([disabled])')
          };
        }
      }
    }

    // Variant ID left empty (or not numeric) by a picker that has no "unselected" UI we know about.
    const idInput = form.querySelector('input[name="id"]');

    if (idInput && !idInput.disabled && !/^\d+$/.test(idInput.value)) {
      const container = form.closest('.product-info')?.querySelector('variant-picker, skechers-variant-picker');
      return { name: '', element: container, focusable: container?.querySelector('input:not([disabled]), button:not([disabled])') };
    }

    return null;
  }

  /**
   * Validate the current selection. When invalid, reuse the theme's error UI: a cart:error event on the form is rendered
   * as an error banner by every <buy-buttons> bound to that form (original and sticky).
   */
  #validateSelection() {
    const missing = this.#getMissingOption();

    if (!missing) {
      return true;
    }

    const now = Date.now();

    if (now - this.#lastErrorAt > ERROR_THROTTLE_MS) {
      this.#lastErrorAt = now;

      const message = missing.name
        ? this.dataset.messageSelectOption.replace(/\{\{\s*option\s*\}\}/, missing.name.toLocaleLowerCase(document.documentElement.lang || undefined))
        : this.dataset.messageSelectVariant;

      this.form?.dispatchEvent(new CustomEvent('cart:error', { bubbles: true, detail: { error: message } }));
    }

    missing.element?.scrollIntoView({
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      block: 'center'
    });

    missing.focusable?.focus({ preventScroll: true });

    return false;
  }

  #onFormSubmit(event) {
    if (event.target !== this.form) {
      return;
    }

    if (!this.#validateSelection()) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }

  /* -------------------------------------------------------------------------------------------------------------------
   * Buy it now
   * ----------------------------------------------------------------------------------------------------------------- */

  #onBuyNowClick(event) {
    event.preventDefault();

    const form = this.form;

    if (!form || this.buyNowButton.disabled || this.buyNowButton.getAttribute('aria-busy') === 'true') {
      return;
    }

    if (!this.#validateSelection()) {
      return;
    }

    // Same native validation the theme's ProductForm runs before submitting (required line item properties, gift card
    // recipient fields…).
    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    const dynamicCheckoutButton = findBuyItNowButton(form);

    if (dynamicCheckoutButton) {
      dynamicCheckoutButton.click();
      return;
    }

    // Fallback: native (non-AJAX) submission of the main form, redirected to checkout by Shopify's /cart/add endpoint.
    this.buyNowButton.setAttribute('aria-busy', 'true');

    let returnTo = form.querySelector('input[name="return_to"]');

    if (!returnTo) {
      returnTo = document.createElement('input');
      returnTo.type = 'hidden';
      returnTo.name = 'return_to';
      form.append(returnTo);
    }

    returnTo.value = `${window.Shopify?.routes?.root || '/'}checkout`;

    // HTMLFormElement.prototype.submit does not fire "submit", so the theme's AJAX handler is intentionally bypassed.
    HTMLFormElement.prototype.submit.call(form);

    // If the visitor comes back (bfcache), the theme's CustomButton resets its loader on "pageshow"; the return_to
    // field is removed too, so that a later "Add to cart" stays an AJAX add.
    window.addEventListener('pageshow', () => returnTo.remove(), { once: true });
  }
}

/**
 * Locate Shopify's unbranded "Buy it now" button inside the main form, supporting both renderings of
 * {{ form | payment_button }}:
 * - legacy markup: <div class="shopify-payment-button"><button class="shopify-payment-button__button--unbranded">
 * - accelerated checkout: <shopify-accelerated-checkout> → <shopify-buy-it-now-button> (open shadow root → <button>)
 * Branded wallets (Shop Pay, PayPal, Apple Pay…) are rendered in iframes and cannot be clicked programmatically, so
 * null is returned and the caller falls back to a native form submission.
 */
function findBuyItNowButton(form) {
  const container = form.querySelector('.shopify-payment-button');

  if (!container || container.getClientRects().length === 0) {
    return null;
  }

  const legacyButton = container.querySelector('button.shopify-payment-button__button--unbranded:not([disabled])');

  if (legacyButton) {
    return legacyButton;
  }

  const buyItNowHost = deepQuery(container, 'shopify-buy-it-now-button');
  return buyItNowHost?.shadowRoot?.querySelector('button:not([disabled])') || null;
}

function deepQuery(root, selector) {
  const match = root.querySelector(selector);

  if (match) {
    return match;
  }

  for (const element of root.querySelectorAll('*')) {
    if (element.shadowRoot) {
      const nested = deepQuery(element.shadowRoot, selector);

      if (nested) {
        return nested;
      }
    }
  }

  return null;
}

if (!window.customElements.get('frg-sticky-buy-buttons')) {
  window.customElements.define('frg-sticky-buy-buttons', FrgStickyBuyButtons);
}
